import { Component, Input, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { Functions, httpsCallable } from '@angular/fire/functions'
import { Auth } from '@angular/fire/auth'
import { AuthCodeGenerationRequest, AuthCodeGenerationResponse, AuthCodeVerificationRequest, AuthCodeVerificationResponse, UserService } from '@nx-superprep/backend/firebase'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { isDevelopment, log } from '@nx-superprep/utils'
import { FirebaseError } from 'firebase/app'
import { Action, ErrorConfig, errorConfigs } from './atuh-transfert-errors'

@Component({
  selector: 'pop-ui-auth-transfer',
  standalone: true,
  templateUrl: './auth-transfer.component.html',
  styleUrls: ['./auth-transfer.component.scss'],
  imports: [CommonModule, MatProgressSpinnerModule, MatButtonModule, MatCardModule],
  providers: [BsModalRef],
})
export class AuthTransferComponent implements OnInit {
  @Input() close: (() => void) = () => this.bsModalRef.hide()

  loading = false
  error = false
  errorMessage = ''
  loadingMessage = ''
  currentErrorConfig: ErrorConfig | null = null
  iconPath = '/assets/brand-icons'
  brandPath = '/assets/brand-logos'
  get icon() { return `${this.iconPath}/${this.destinationAppName}.svg` }
  get brand() { return `${this.brandPath}/${this.destinationAppName}.svg` }

  // allowed domains
  private readonly ALLOWED_DOMAINS = isDevelopment()
  ? [
      'localhost',
      'nx-popstudy.herokuapp.com',
      'superprep-quiz.web.app',
      'www.ltclassroom-dev.com',
      'superprep-stack-git-main-longtail-education.vercel.app',
    ]
  : [
      'www.popstudy.com',
      'www.popquiz.io',
      'www.popteams.com',
      'www.popstack.it',
    ]

  private nameForDomain = {
    'www.popstudy.com': 'popstudy',
    'www.popquiz.io': 'popquiz',
    'www.popteams.com': 'popteams',
    'www.popstack.it': 'popstack',
    'localhost': 'popstudy',
    'nx-popstudy.herokuapp.com': 'popstudy',
    'superprep-quiz.web.app': 'popquiz',
    'www.ltclassroom-dev.com': 'popteams',
    'superprep-stack-git-main-longtail-education.vercel.app': 'popstack',
  }

  get destinationAppName() {
    const domain = new URL(this.destinationUrl as string).hostname
    return !this.errorMessage ? this.nameForDomain[domain as keyof typeof this.nameForDomain] || 'popstudy' : 'popstudy'
  }

  private destinationUrl: string | null = null
  private code: string | null = null

  //readonly functions = inject(Functions)
  //readonly auth = inject(Auth)
  //readonly route = inject(ActivatedRoute)
  //readonly router = inject(Router)
  //readonly userService = inject(UserService)
  //readonly bsModalRef = inject(BsModalRef)

  // Inject the services via the constructor instead of using `inject`
  constructor(
    private functions: Functions,
    private auth: Auth,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private bsModalRef: BsModalRef
  ) {}

  ngOnInit() {
    this.validateAndInitiate()
  }

  private validateAndInitiate() {
    this.code = this.route.snapshot.queryParamMap.get('code')
    this.destinationUrl = this.route.snapshot.queryParamMap.get('destinationUrl')

    if (!this.destinationUrl) {
      this.handleError(this.code ? 'missing-params' : 'missing-destination')
      return
    }

    try {
      const url = new URL(this.destinationUrl)
      if (!this.ALLOWED_DOMAINS.includes(url.hostname)) {
        this.handleError('invalid-domain')
        return
      }
    } catch {
      this.handleError('invalid-domain')
      return
    }

    if (this.code) {
      this.handleAuthTransfer()
    } else {
      this.initiateAuthTransfer()
    }
  }

  private async handleAuthTransfer() {
    this.loading = true
    this.loadingMessage = 'Completing secure transfer...'

    try {
      const verifyAuthCode = httpsCallable<AuthCodeVerificationRequest, AuthCodeVerificationResponse>(
        this.functions,
        'verify_auth_code'
      )
      const verifyResult = await verifyAuthCode({ code: this.code as string })

      if (!verifyResult.data.customToken) {
        throw new FirebaseError('invalid-argument', 'Invalid token received')
      }

      await this.userService.signInWithClassroomId({ authToken: verifyResult.data.customToken })
      const finalDestination = new URL(this.destinationUrl as string).pathname
      await this.router.navigate([finalDestination])
      this.close()
    } catch (error) {
      if (error instanceof FirebaseError) {
        this.handleError(error.code, error.message)
      } else {
        this.handleError('internal')
      }
    }
  }

  private async initiateAuthTransfer() {
    this.loading = true
    this.loadingMessage = 'Preparing secure transfer...'

    try {
      const currentUser = await this.auth.currentUser
      if (!currentUser) {
        this.handleError('no-user')
        return
      }

      let idToken: string
      try {
        idToken = await currentUser.getIdToken()
      } catch (error) {
        this.handleError('token-error', (error as FirebaseError).message)
        return
      }

      const generateAuthCode = httpsCallable<AuthCodeGenerationRequest, AuthCodeGenerationResponse>(
        this.functions,
        'request_auth_code'
      )

      const response = await generateAuthCode({ idToken })

      const destinationAuthUrl = new URL(this.destinationUrl as string)
      destinationAuthUrl.pathname = '/auth_transfer'
      destinationAuthUrl.searchParams.set('code', response.data.code)
      destinationAuthUrl.searchParams.set('destinationUrl', this.destinationUrl as string)
      window.location.href = destinationAuthUrl.toString()
    } catch (error) {
      if (error instanceof FirebaseError) {
        this.handleError(error.code, error.message)
      } else {
        this.handleError('internal')
      }
    }
  }

  private handleError(code: string, message?: string) {
    this.loading = false
    this.error = true
    this.currentErrorConfig = errorConfigs[code]
    this.errorMessage = message || this.currentErrorConfig?.message || 'An unexpected error occurred'
    log.warn(`Auth transfer error: ${code} - ${this.errorMessage}`)
  }

  handleAction(action: Action) {
    if (!action) return

    if (action.type === 'primary') {
      if (action.action === 'retry') {
        this.error = false
        this.errorMessage = ''
        this.currentErrorConfig = null
        this.validateAndInitiate()
      } else if (action.action === 'navigate') {
        this.router.navigate([action.path])
        this.close()
      }
    } else if (action.type === 'secondary') {
      if (action.action === 'navigate') {
        this.router.navigate([action.path])
        this.close()
      }
    }
  }

  setButtonBackgroundColor(event: Event, color: string) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = color;
    }
  }
}
